import { Typography } from 'antd';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import syringeIcon from '../../../../../assets/images/syringe-icon.png';
import { CHART_COLORS, SVG_SHADOW_FILTER } from '../../../constants/styles';
import { DonationGoalData } from '../../../types/phlebotomist-dashboard';

const { Text } = Typography;

interface IDonationGoalGaugeProps {
  /** Goal data from context */
  goalData: DonationGoalData;
  /** Actual successful donations for this phlebotomist */
  successfulDonations: number;
  /** Date range label for display */
  dateRangeLabel?: string;
  /** Whether to use compact layout */
  isCompact?: boolean;
  /** Comparison successful donations for inline mode */
  comparisonSuccessfulDonations?: number;
  /** Comparison goal data for inline mode */
  comparisonGoalData?: DonationGoalData | null;
  /** Comparison date range label for inline mode */
  comparisonDateRangeLabel?: string;
  /** Phlebotomist name for title (admin/MD viewing specific phlebotomist) */
  phlebotomistName?: string;
}

/**
 * DonationGoalGauge - Semi-circular gauge showing progress toward donation collection goal
 * Similar design to fitness app goal trackers
 */
export const DonationGoalGauge: FC<IDonationGoalGaugeProps> = ({
  goalData,
  successfulDonations,
  dateRangeLabel,
  isCompact = false,
  comparisonSuccessfulDonations,
  comparisonGoalData,
  comparisonDateRangeLabel,
  phlebotomistName,
}) => {
  const { t } = useTranslation('dashboard.PhlebotomistDashboard');

  // Calculate progress percentage (allow >100%)
  const progressPercent = useMemo(() => {
    if (goalData.goalQty <= 0) return 0;
    return (successfulDonations / goalData.goalQty) * 100;
  }, [successfulDonations, goalData.goalQty]);

  // Capped progress for the 0-100% colored segments
  const cappedProgressPercent = Math.min(progressPercent, 100);

  // Comparison progress percentage
  const comparisonProgressPercent = useMemo(() => {
    if (!comparisonGoalData || !comparisonSuccessfulDonations) return undefined;
    if (comparisonGoalData.goalQty <= 0) return 0;
    return (comparisonSuccessfulDonations / comparisonGoalData.goalQty) * 100;
  }, [comparisonSuccessfulDonations, comparisonGoalData]);

  const hasComparison =
    comparisonSuccessfulDonations !== undefined && comparisonGoalData;

  // Format dates for display
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const dateRange = useMemo(() => {
    return {
      start: formatDate(goalData.dateRangeStart),
      end: formatDate(goalData.dateRangeEnd),
    };
  }, [goalData.dateRangeStart, goalData.dateRangeEnd]);

  // Comparison date range
  const comparisonDateRange = useMemo(() => {
    if (!comparisonGoalData) return null;
    return {
      start: formatDate(comparisonGoalData.dateRangeStart),
      end: formatDate(comparisonGoalData.dateRangeEnd),
    };
  }, [comparisonGoalData]);

  // Format team count text - returns JSX with line break
  const teamCountText = useMemo(() => {
    const count = goalData.teamCount;
    if (count === 1) {
      return (
        <>
          {t(
            'goalGauge.teamCountLine1Single',
            'There is {{count}} Phlebotomist',
            { count }
          )}
          <br />
          {t('goalGauge.teamCountLine2', 'on your team.')}
        </>
      );
    }
    return (
      <>
        {t(
          'goalGauge.teamCountLine1Plural',
          "There are {{count}} Phlebotomist's",
          { count }
        )}
        <br />
        {t('goalGauge.teamCountLine2', 'on your team.')}
      </>
    );
  }, [goalData.teamCount, t]);

  // Comparison team count text - returns JSX with line break
  const comparisonTeamCountText = useMemo(() => {
    if (!comparisonGoalData) return null;
    const count = comparisonGoalData.teamCount;
    if (count === 1) {
      return (
        <>
          {t(
            'goalGauge.teamCountLine1Single',
            'There is {{count}} Phlebotomist',
            { count }
          )}
          <br />
          {t('goalGauge.teamCountLine2', 'on your team.')}
        </>
      );
    }
    return (
      <>
        {t(
          'goalGauge.teamCountLine1Plural',
          "There are {{count}} Phlebotomist's",
          { count }
        )}
        <br />
        {t('goalGauge.teamCountLine2', 'on your team.')}
      </>
    );
  }, [comparisonGoalData, t]);

  // Progress color - using brand colors - deep navy for progress, royal blue for overflow
  const progressColor = useMemo(() => {
    if (progressPercent > 100) return CHART_COLORS.gauge.overflow; // Royal blue for overflow
    return CHART_COLORS.gauge.primary; // Deep navy for normal progress
  }, [progressPercent]);

  // SVG dimensions for the 250-degree arc gauge
  const size = isCompact ? 140 : 180;
  const strokeWidth = isCompact ? 15 : 20; // 25% wider than before (12->15, 16->20)
  const radius = (size - strokeWidth) / 2;

  // Full circle approach: segments go clockwise from start point
  // Base arc = 250 degrees for 100%, overflow extends further
  const baseArcAngle = 250;
  const fullCircumference = 2 * Math.PI * radius;
  const baseCircumference = (baseArcAngle / 360) * fullCircumference;

  // Center point for the SVG
  const cx = size / 2;
  const cy = size / 2;

  // Calculate overflow for >100% using non-linear scaling
  const overflowPercent = Math.max(0, progressPercent - 100);
  // Scale overflow: exponent 0.75 gives sublinear growth (early overflow visible, high overflow compressed)
  const overflowAngle =
    overflowPercent > 0 ? Math.pow(overflowPercent, 0.75) * 1 : 0;
  const maxOverflowAngle = 360 - baseArcAngle; // 130 degrees max
  const cappedOverflowAngle = Math.min(overflowAngle, maxOverflowAngle);
  const overflowArcLength = (cappedOverflowAngle / 360) * fullCircumference;

  // Start point: bottom-left of the gauge
  // Rotate clockwise to position gap at bottom
  const startAngleDeg = 235; // Adjusted by 20deg for 250deg arc

  // Brand color scheme - deep navy for progress, royal blue for overflow
  const BLUE_COLOR = CHART_COLORS.gauge.primary;
  const DARK_BLUE_COLOR = CHART_COLORS.gauge.overflow; // Royal blue for overflow (>100%)

  // Calculate the arc length for progress (single blue bar)
  const progressArcLength = useMemo(() => {
    // Convert progress percentage to arc length (based on base circumference for 100%)
    return (cappedProgressPercent / 100) * baseCircumference;
  }, [cappedProgressPercent, baseCircumference]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: isCompact ? 8 : 12,
        backgroundColor: '#f5f5f5',
        borderRadius: 8,
        border: '1px solid #e8e8e8',
        minWidth: isCompact ? 160 : 200,
        overflow: 'hidden',
      }}
    >
      {/* Header box - styled like the reference image */}
      <div
        style={{
          width: '100%',
          backgroundColor: CHART_COLORS.gauge.header,
          borderRadius: '4px 4px 0 0',
          padding: isCompact ? '4px 8px' : '6px 12px',
          marginBottom: 4,
        }}
      >
        <Text
          strong
          style={{
            fontSize: isCompact ? 11 : 13,
            color: '#ffffff',
            display: 'block',
            textAlign: phlebotomistName ? 'center' : 'left',
          }}
        >
          {phlebotomistName ? (
            <>
              {phlebotomistName}&apos;s
              <br />
              {t('goalGauge.titleLine2', 'Donation Collection Goals')}
            </>
          ) : (
            t('goalGauge.title', 'Your Donation Collection Goals')
          )}
        </Text>
      </div>

      {/* Team count text - centered */}
      <Text
        strong
        style={{
          fontSize: isCompact ? 10 : 12,
          color: '#595959',
          marginBottom: hasComparison ? 0 : 2,
          textAlign: 'center',
          width: '100%',
        }}
      >
        {teamCountText}
      </Text>
      {/* Comparison team count text */}
      {hasComparison && comparisonTeamCountText && (
        <Text
          style={{
            fontSize: isCompact ? 9 : 11,
            color: '#8c8c8c',
            marginBottom: 2,
            textAlign: 'center',
            width: '100%',
          }}
        >
          {comparisonTeamCountText}
        </Text>
      )}

      {/* "Between:" label */}
      <Text
        style={{
          fontSize: isCompact ? 9 : 11,
          color: '#595959',
          marginBottom: 2,
          textAlign: 'center',
          width: '100%',
        }}
      >
        {t('goalGauge.between', 'Between:')}
      </Text>

      {/* Date range */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          fontSize: isCompact ? 9 : 11,
          fontWeight: 600,
          color: '#595959',
          marginBottom: hasComparison ? 0 : 4,
          padding: '0 8px',
        }}
      >
        <span>{dateRange.start}</span>
        <span>{dateRange.end}</span>
      </div>
      {/* Comparison date range */}
      {hasComparison && comparisonDateRange && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            fontSize: isCompact ? 8 : 10,
            color: '#8c8c8c',
            marginBottom: 4,
            padding: '0 8px',
          }}
        >
          <span>{comparisonDateRange.start}</span>
          <span>{comparisonDateRange.end}</span>
        </div>
      )}

      {/* Arc gauge */}
      <div
        style={{
          position: 'relative',
          width: size,
          // Taller to fit content inside arc with office share and performance text
          // Add extra height when comparison data is shown
          height: hasComparison ? size * 1.55 : size * 1.4,
        }}
      >
        <svg width={size} height={size} style={{ overflow: 'visible' }}>
          {/* Drop shadow filter for gauge circles - uses shared settings */}
          <defs>
            <filter
              id={SVG_SHADOW_FILTER.id}
              x="-50%"
              y="-50%"
              width="160%"
              height="160%"
            >
              <feDropShadow
                dx={SVG_SHADOW_FILTER.dx}
                dy={SVG_SHADOW_FILTER.dy}
                stdDeviation={SVG_SHADOW_FILTER.stdDeviation}
                floodOpacity={SVG_SHADOW_FILTER.floodOpacity}
              />
            </filter>
          </defs>
          {/*
            Circle-based gauge: all segments use the same circle, rotated to start at bottom-left.
            strokeDasharray creates the segments, strokeDashoffset positions them.
            Rotation: -90deg puts start at top, then add startAngleDeg to position at bottom-left.
          */}
          <g
            transform={`rotate(${startAngleDeg - 90} ${cx} ${cy})`}
            filter={`url(#${SVG_SHADOW_FILTER.id})`}
          >
            {/* Background arc (gray) - shows the base 250° arc */}
            <circle
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke="#e8e8e8"
              strokeWidth={strokeWidth}
              strokeDasharray={`${baseCircumference} ${fullCircumference}`}
              strokeLinecap="butt"
            />
            {/* Overflow segment (>100%) - dark blue with rounded end, rendered BEFORE blue so blue is on top */}
            {overflowPercent > 0 && overflowArcLength > 0 && (
              <circle
                cx={cx}
                cy={cy}
                r={radius}
                fill="none"
                stroke={DARK_BLUE_COLOR}
                strokeWidth={strokeWidth}
                strokeDasharray={`${overflowArcLength} ${fullCircumference}`}
                strokeDashoffset={-baseCircumference}
                strokeLinecap="round"
              />
            )}
            {/* Blue progress segment (0-100%) */}
            {progressArcLength > 0 && (
              <circle
                cx={cx}
                cy={cy}
                r={radius}
                fill="none"
                stroke={BLUE_COLOR}
                strokeWidth={strokeWidth}
                strokeDasharray={`${progressArcLength} ${fullCircumference}`}
                strokeLinecap="butt"
              />
            )}
            {/* Progress marker line - shows current progress position
                Rendered LAST so it draws on top of everything
                Radius adjusted so outer edge aligns with other circles
            */}
            {(() => {
              const markerStrokeWidth = strokeWidth * 1.25;
              // Adjust radius so outer edge matches: radius + strokeWidth/2 = markerRadius + markerStrokeWidth/2
              const markerRadius =
                radius + (strokeWidth - markerStrokeWidth) / 2;
              const markerCircumference = 2 * Math.PI * markerRadius;
              // Position at end of current progress (capped at 100%)
              const progressArcLength =
                (cappedProgressPercent / 100) *
                (baseArcAngle / 360) *
                markerCircumference;
              return (
                <circle
                  cx={cx}
                  cy={cy}
                  r={markerRadius}
                  fill="none"
                  stroke="black"
                  strokeWidth={markerStrokeWidth}
                  strokeDasharray={`2 ${markerCircumference}`}
                  strokeDashoffset={-progressArcLength + 1}
                  strokeOpacity={0.5}
                />
              );
            })()}
          </g>
        </svg>

        {/* Center content - all inside the arc, NUMBER centered vertically */}
        <div
          style={{
            position: 'absolute',
            top: '20%',
            left: '50%',
            transform: 'translate(-50%, 0)',
            textAlign: 'center',
          }}
        >
          <Text
            strong
            style={{
              fontSize: isCompact ? 9 : 11,
              display: 'block',
              color: '#595959',
            }}
          >
            {t('goalGauge.successful', 'Your Successful')}
          </Text>
          <Text
            strong
            style={{
              fontSize: isCompact
                ? hasComparison
                  ? 18
                  : 22
                : hasComparison
                ? 22
                : 28,
              color: progressColor,
              lineHeight: 1,
              display: 'block',
            }}
          >
            {goalData.donationSharePercent}%
          </Text>
          {/* Comparison donation share */}
          {hasComparison && comparisonGoalData && (
            <Text
              style={{
                fontSize: isCompact ? 12 : 14,
                color: '#626262',
                lineHeight: 1,
                display: 'block',
                marginTop: 2,
              }}
            >
              {comparisonGoalData.donationSharePercent}%
            </Text>
          )}
          <Text
            strong
            style={{
              fontSize: isCompact ? 9 : 11,
              color: '#595959',
              display: 'block',
            }}
          >
            {t('goalGauge.donationShare', 'Donation Share')}
          </Text>
          {/* Syringe icon in brand-colored button-like square */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: isCompact ? 20 : 26,
              height: isCompact ? 20 : 26,
              backgroundColor: CHART_COLORS.FFP.expected, // Light sage from brand
              border: `1px solid ${CHART_COLORS.gauge.primary}`, // Deep navy border
              borderRadius: 4,
              marginTop: hasComparison ? 6 : 10,
              marginBottom: isCompact ? 6 : 8,
              padding: 4,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            }}
          >
            <img
              src={syringeIcon}
              alt="syringe"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </div>
          {/* Progress text inside arc - Office Share and performance */}
          {/* When progress > 100%, add extra 25px margin to make room for overflow arc */}
          <div
            style={{
              marginTop: hasComparison ? 2 : progressPercent > 100 ? 31 : 6,
              whiteSpace: 'nowrap',
              lineHeight: 1.2,
              maxWidth: 200,
              display: 'inline-block', // Centers via parent's textAlign: 'center'
            }}
          >
            <Text
              style={{
                fontSize: isCompact ? 10 : 12,
                color: '#595959',
                display: 'block',
              }}
            >
              {t('goalGauge.officeShareLabel', 'Your Goal Share is ')}{' '}
              <Text strong style={{ fontSize: isCompact ? 10 : 12 }}>
                {goalData.officeSharePercent}%
              </Text>
            </Text>
            <Text
              strong
              style={{
                fontSize: isCompact ? 10 : 12,
                color: progressColor,
                display: 'block',
                marginTop: 2,
              }}
            >
              {t('goalGauge.performingAtLine1', 'You are performing at')}
              <br />
              {t('goalGauge.performingAtLine2', '{{percent}}% of your goal', {
                percent: progressPercent.toFixed(1),
              })}
            </Text>
          </div>
          {/* Comparison progress text */}
          {hasComparison &&
            comparisonProgressPercent !== undefined &&
            comparisonGoalData && (
              <div
                style={{
                  marginTop: 4,
                  whiteSpace: 'normal',
                  maxWidth: isCompact ? 180 : 200,
                  lineHeight: 1.2,
                }}
              >
                <Text
                  style={{
                    fontSize: isCompact ? 8 : 10,
                    color: '#8c8c8c',
                    display: 'block',
                  }}
                >
                  {t(
                    'goalGauge.officeShare',
                    'Your gaol Share is {{percent}}%',
                    {
                      percent: comparisonGoalData.officeSharePercent,
                    }
                  )}
                </Text>
                <Text
                  style={{
                    fontSize: isCompact ? 8 : 10,
                    color: '#626262',
                    display: 'block',
                  }}
                >
                  {t(
                    'goalGauge.performingAt',
                    'You are performing at {{percent}}% of your goal',
                    {
                      percent: comparisonProgressPercent.toFixed(1),
                    }
                  )}
                </Text>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default DonationGoalGauge;
